import * as Scrivito from 'scrivito';
import { PodcastWidget, PodcastWidgetClass } from './podcast-widget-class';
import { PodcastWidgetAttributes } from './podcast-widget-definitions';
import styles from './podcast-widget.module.scss';
import React, { ElementType, useRef, useState } from 'react';
import {
  BackwardButton,
  ForwardButton,
  PlayPauseButton,
  PodcastSlider,
  PodcastVolume,
} from './podcast-widget.components';
import parse from 'html-react-parser';
import { ImageObjectAttributes } from '../../objects';
import { spacingClassName } from '../../utils/scrivito/spacing-definitions';
import { HeadlineWidgetTag, HeadlineWidgetVariant } from '../headline-widget';
import { Typography } from '@mui/material';
import classNames from 'classnames';

function formatTime(value: number): string {
  const minutes = Math.floor(value / 60);
  const seconds = Math.floor(value % 60);
  return minutes.toString().padStart(2, '0') + ':' + seconds.toString().padStart(2, '0');
}

export const PodcastWidgetComponent: React.FC<{ widget: PodcastWidget }> = ({ widget }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);

  const title = widget.get(PodcastWidgetAttributes.TITLE);
  const titleType = (widget.get(PodcastWidgetAttributes.TITLE_COMPONENT) ?? HeadlineWidgetTag.H2) as ElementType;
  const text = widget.get(PodcastWidgetAttributes.TEXT);
  const image = widget.get(PodcastWidgetAttributes.IMAGE);
  const file = widget.get(PodcastWidgetAttributes.FILE);
  const theme = widget.get(PodcastWidgetAttributes.THEME);

  const handleTimeUpdate = (): void => {
    setCurrentTime(audioRef.current?.currentTime ?? 0);
  };

  const handleLoadedMetadata = (): void => {
    setDuration(audioRef.current?.duration ?? 0);
  };

  const handleSliderPositionSet = (position: number): void => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = position;
    }
  };

  return (
    <Scrivito.WidgetTag className={styles.PodcastWidget} data-audio-theme={theme}>
      <div className={spacingClassName(widget)}>
        <div className={styles.PodcastWidgetWrapper}>
          {title && (
            <Typography variant={HeadlineWidgetVariant.L} component={titleType} className={styles.PodcastWidgetTitle}>
              {title}
            </Typography>
          )}
          {text && <div className={styles.PodcastWidgetText}>{parse(text)}</div>}

          {image && (
            <img
              src={image.contentUrl()}
              alt={image.get(ImageObjectAttributes.ALTERNATIVE_TEXT) as string}
              className={styles.PodcastWidgetImage}
            />
          )}

          {file && (
            <>
              <div
                className={classNames(styles.PodcastWidgetControls, {
                  [styles.PodcastWidgetControlsIPhone]: /iPhone|iPad|iPod/i.test(navigator.userAgent),
                })}
              >
                <div className={styles.PodcastWidgetButtons}>
                  <BackwardButton audioRef={audioRef} />
                  <PlayPauseButton audioRef={audioRef} />
                  <ForwardButton audioRef={audioRef} />
                </div>

                <div className={styles.PodcastWidgetSlider}>
                  <PodcastSlider
                    position={currentTime}
                    duration={duration}
                    onChangePosition={handleSliderPositionSet}
                  />
                  <div className={styles.PodcastWidgetSliderTime}>
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </div>
                  <div className={styles.PodcastWidgetSliderTimeSmall}>{formatTime(currentTime)}</div>
                </div>

                <PodcastVolume
                  volume={volume}
                  onChangeVolume={(volume): void => {
                    const audio = audioRef.current;
                    if (audio) {
                      audio.volume = volume;
                      setVolume(volume);
                    }
                  }}
                />
              </div>

              <audio
                ref={audioRef}
                src={file.url()}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
              />
            </>
          )}
        </div>
      </div>
    </Scrivito.WidgetTag>
  );
};

Scrivito.provideComponent(PodcastWidgetClass, PodcastWidgetComponent);
